import React, { createContext, useContext, useState, ReactNode } from "react";
import { SimpleOrder } from "../interfaces/simpleOrder";
import { Order } from "../interfaces/order";
interface CompletedOrdersContextType {
  completedOrders: SimpleOrder[];
  setCompletedOrders: React.Dispatch<React.SetStateAction<SimpleOrder[]>>;
  pageNumber: number;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  searchApplied: string;
  setSearchApplied: React.Dispatch<React.SetStateAction<string>>;
  startDate: string | null;
  setStartDate: React.Dispatch<React.SetStateAction<string | null>>;
  endDate: string | null;
  setEndDate: React.Dispatch<React.SetStateAction<string | null>>;
  orderType: string | null;
  setOrderType: React.Dispatch<React.SetStateAction<string | null>>;
  searchType: string | null;
  setSearchType: React.Dispatch<React.SetStateAction<string | null>>;
  appliedStartDate: string | null;
  setAppliedStartDate: React.Dispatch<React.SetStateAction<string | null>>;
  appliedEndDate: string | null;
  setAppliedEndDate: React.Dispatch<React.SetStateAction<string | null>>;
  appliedOrderType: string | null;
  setAppliedOrderType: React.Dispatch<React.SetStateAction<string | null>>;
  appliedSearchType: string | null;
  setAppliedSearchType: React.Dispatch<React.SetStateAction<string | null>>;
  hasMore: boolean;
  setHasMore: React.Dispatch<React.SetStateAction<boolean>>;
  updateOrderInCompletedOrdersList: (
    updatedOrder: Order,
    statusID: number,
  ) => void;
}

const CompletedOrdersContext = createContext<
  CompletedOrdersContextType | undefined
>(undefined);

export const CompletedOrdersProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [completedOrders, setCompletedOrders] = useState<SimpleOrder[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [orderType, setOrderType] = useState<string | null>(null);
  const [searchType, setSearchType] = useState<string | null>("Candidate");
  const [appliedStartDate, setAppliedStartDate] = useState<string | null>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<string | null>(null);
  const [appliedOrderType, setAppliedOrderType] = useState<string | null>(null);
  const [appliedSearchType, setAppliedSearchType] = useState<string | null>(
    "Candidate",
  );
  const [hasMore, setHasMore] = useState(true);

  const updateOrderInCompletedOrdersList = (order: Order, statusID: number) => {
    if (statusID !== 4) {
      setCompletedOrders((prev) =>
        prev.filter((o) => o.orderID !== order.orderID),
      );
    } else {
      setCompletedOrders((prev) =>
        prev.map((o) =>
          o.orderID === order.orderID
            ? {
                ...o,
                candidateFirstName: order.candidateFirstName,
                candidateLastName: order.candidateLastName,
                candidateMI: order.candidateMI,
                designation: order.designation ?? undefined,
                orderStatusID: statusID,
                completedDate: new Date(),
              }
            : o,
        ),
      );
    }
  };

  return (
    <CompletedOrdersContext.Provider
      value={{
        completedOrders,
        setCompletedOrders,
        pageNumber,
        setPageNumber,
        searchTerm,
        setSearchTerm,
        searchApplied,
        setSearchApplied,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        orderType,
        setOrderType,
        searchType,
        setSearchType,
        appliedStartDate,
        setAppliedStartDate,
        appliedEndDate,
        setAppliedEndDate,
        appliedOrderType,
        setAppliedOrderType,
        appliedSearchType,
        setAppliedSearchType,
        hasMore,
        setHasMore,
        updateOrderInCompletedOrdersList,
      }}
    >
      {children}
    </CompletedOrdersContext.Provider>
  );
};

export const useCompletedOrdersContext = () => {
  const context = useContext(CompletedOrdersContext);
  if (!context)
    throw new Error(
      "useCompletedOrdersContext must be used within OrderProvider",
    );
  return context;
};
